import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../api.js';
import {
  isDateField,
  isTagsField,
  isRiskScoreField,
  isComputerTypeField,
  getRiskScoreLabel,
  getUserComputerType
} from '../components/utils';
import DashboardCounters from '../components/Labels';
import { useAuth } from '../context/AuthContext';
import { useRef } from 'react';
import DataTable from '../components/dashboard/DataTable';
import TimeSeriesChart from '../components/dashboard/TimeSeriesChart';
import SankeyDiagram from '../components/dashboard/SankeyDiagram';
import SankeyDiagramInfo from '../components/dashboard/SankeyDiagramInfo';
import DashboardSidebar from '../components/DashboardSidebar';

// Helper to extract risk score for a user from the array
function getUserRiskScore(riskScoreArr, userId) {
  if (Array.isArray(riskScoreArr)) {
    const userEntry = riskScoreArr.find(e => e.user_id === userId);
    if (userEntry) return userEntry.risk_score;
    const defaultEntry = riskScoreArr.find(e => e.user_id === 0);
    if (defaultEntry) return defaultEntry.risk_score;
    return undefined;
  }
  return riskScoreArr;
}

const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowUpdateError, setRowUpdateError] = useState('');
  const [tagsOptions, setTagsOptions] = useState([]);
  const [pendingSolvedUpdates, setPendingSolvedUpdates] = useState({});
  const debounceTimer = useRef(null);
  const pendingSolvedUpdatesRef = useRef(pendingSolvedUpdates);

  // --- Risk Score Batch Update State ---
  const [pendingRiskScoreUpdates, setPendingRiskScoreUpdates] = useState({});
  const riskScoreDebounceTimer = useRef(null);
  const pendingRiskScoreUpdatesRef = useRef(pendingRiskScoreUpdates);

  // --- Computer Type Batch Update State ---
  const [pendingComputerTypeUpdates, setPendingComputerTypeUpdates] = useState({});
  const computerTypeDebounceTimer = useRef(null);
  const pendingComputerTypeUpdatesRef = useRef(pendingComputerTypeUpdates);

  // --- Domain Type Filter State ---
  const [domainTypeFilter, setDomainTypeFilter] = useState('all'); // 'all', 'primary', 'third'

  // --- Record Type Filter State ---
  const [recordTypeFilter, setRecordTypeFilter] = useState('all'); // 'all', 'domains', 'urls'

  // --- URL Type Filter State ---
  const [urlTypeFilter, setUrlTypeFilter] = useState('all'); // 'all', 'employee', 'user'

  // --- Counter Filter State ---
  const [activeCounterFilter, setActiveCounterFilter] = useState(null); 

  // --- Table State ---
  const [tableFilters, setTableFilters] = useState({});
  const [tableSortField, setTableSortField] = useState('added');
  const [tableSortOrder, setTableSortOrder] = useState('desc');
  const [tablePasswordVisibility, setTablePasswordVisibility] = useState({});
  const [tableHoveredLink, setTableHoveredLink] = useState(null);
  const [tableHoveredEye, setTableHoveredEye] = useState(null);
  const [tableShowTagsDropdown, setTableShowTagsDropdown] = useState(false);
  const [tableDatePopover, setTableDatePopover] = useState({ field: null, anchor: null });

  // --- Sankey Filter State ---
  const [sankeyFilter, setSankeyFilter] = useState(null);

  // --- Chart Expand State ---
  const [expandedChart, setExpandedChart] = useState(null); // 'timeSeries', 'sankey', or null
  const [chartExpansionLevel, setChartExpansionLevel] = useState({}); // Track expansion level for each chart

  // --- Sidebar State ---
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(90); // Default to 90 days
  const [selectedDomain, setSelectedDomain] = useState(''); // Specific domain filter

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    pendingSolvedUpdatesRef.current = pendingSolvedUpdates;
  }, [pendingSolvedUpdates]);

  useEffect(() => {
    pendingRiskScoreUpdatesRef.current = pendingRiskScoreUpdates;
  }, [pendingRiskScoreUpdates]);

  useEffect(() => {
    pendingComputerTypeUpdatesRef.current = pendingComputerTypeUpdates;
  }, [pendingComputerTypeUpdates]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const dashboard = await api.getDashboard();
      setData(dashboard.recent_results || []);
      setTagsOptions(dashboard.user_tags || []);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const timeSeriesData = useMemo(() => {
    const counts = {};
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    
    data.forEach((rec) => {
      // Apply record type filter
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return;
      if (rec.bucket === 'leaks.logs' && rec.added) {
        const recordDate = new Date(rec.added);
        if (recordDate >= cutoffDate) {
          const day = rec.added.slice(0, 10);
          counts[day] = (counts[day] || 0) + 1;
        }
      }
    });

    const arr = [];
    const now = new Date();
    for (let i = selectedDateRange - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const day = d.toISOString().slice(0, 10);
      arr.push({ date: day, count: counts[day] || 0 });
    }
    return arr;
  }, [data, selectedDateRange, recordTypeFilter]);

  const primaryRecords = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (rec.domain_type !== 1) return false;
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    });
  }, [data, selectedDateRange]);

  const thirdPartyRecords = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (rec.domain_type !== 2) return false;
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    });
  }, [data, selectedDateRange]);

  const highRiskEmployeeInfections = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || (getUserRiskScore(rec.risk_score, user?.id) < 3)) return false;
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);
  
  const personalComputerInfections = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || getUserComputerType(rec.computer_type, user?.id) !== 'PERSONAL') return false;
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, selectedDateRange, domainTypeFilter, selectedDomain, user?.id, recordTypeFilter, urlTypeFilter]);

  const corporateComputerInfections = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || getUserComputerType(rec.computer_type, user?.id) !== 'CORPORATE') return false;
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, selectedDateRange, domainTypeFilter, selectedDomain, user?.id, recordTypeFilter, urlTypeFilter]);

  const attackerInfections = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || getUserComputerType(rec.computer_type, user?.id) !== 'ATTACKER') return false;
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, selectedDateRange, domainTypeFilter, selectedDomain, user?.id, recordTypeFilter, urlTypeFilter]);

  const thirdPartyInfoStealers = useMemo(() => {
    return thirdPartyRecords.filter(rec => rec.bucket === 'leaks.logs').length;
  }, [thirdPartyRecords]);

  const externalPageCredentials = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || !rec.external) return false;
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const outsourcingInfections = useMemo(() => {
    return primaryRecords.filter(rec => rec.outsource === true).length;
  }, [primaryRecords]);

  const UserInfections = useMemo(() => {
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'reverse.leaks.logs' || !(rec.url_type & 2)) return false;
      return true;
    }).length;
  }, [data, recordTypeFilter]);

  // --- Unsolved Records Calculations ---
  const highRiskEmployeeInfectionsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || (getUserRiskScore(rec.risk_score, user?.id) < 3)) return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);
  
  const personalComputerInfectionsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || getUserComputerType(rec.computer_type, user?.id) !== 'PERSONAL') return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const corporateComputerInfectionsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || getUserComputerType(rec.computer_type, user?.id) !== 'CORPORATE') return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const attackerInfectionsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || getUserComputerType(rec.computer_type, user?.id) !== 'ATTACKER') return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const thirdPartyInfoStealersUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.domain_type !== 2 || rec.bucket !== 'leaks.logs') return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const externalPageCredentialsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'leaks.logs' || !rec.external) return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false;
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const outsourcingInfectionsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.domain_type !== 1 || rec.outsource !== true) return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      
      // Apply domain type filters only when record type is 'domains'
      if (recordTypeFilter === 'domains') {
        if (domainTypeFilter === 'primary' && rec.domain_type !== 1) return false;
        if (domainTypeFilter === 'third' && rec.domain_type !== 2) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      // Apply URL type filters only when record type is 'urls'
      if (recordTypeFilter === 'urls') {
        if (urlTypeFilter === 'employee' && !(rec.url_type & 1)) return false;
        if (urlTypeFilter === 'user' && !(rec.url_type & 2)) return false;
        if (selectedDomain && rec.domain !== selectedDomain) return false;
      }
      
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    }).length;
  }, [data, user?.id, selectedDateRange, domainTypeFilter, selectedDomain, recordTypeFilter, urlTypeFilter]);

  const UserInfectionsUnsolved = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    return data.filter(rec => {
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (rec.bucket !== 'reverse.leaks.logs' || !(rec.url_type & 2)) return false;
      if (Array.isArray(rec.solved) && rec.solved.includes(user?.id)) return false; // Skip if user has solved it
      return true;
    }).length;
  }, [data, user?.id, selectedDateRange, recordTypeFilter]);

  // Extract available domains from data for the domain filter (only from domain findings)
  const availableDomains = useMemo(() => {
    const domains = new Set();
    data.forEach(rec => {
      if (rec.domain && rec.domain_type !== undefined) {
        domains.add(rec.domain);
      }
    });
    return Array.from(domains).sort();
  }, [data]);

  // Extract available URLs from data for the URL filter (only from URL findings)
  const availableUrls = useMemo(() => {
    const urls = new Set();
    data.forEach(rec => {
      if (rec.domain && rec.url_type !== undefined) {
        urls.add(rec.domain);
      }
    });
    return Array.from(urls).sort();
  }, [data]);


  const applyCounterFilter = useCallback((records) => {
    if (!activeCounterFilter) return records;
    switch (activeCounterFilter) {
      case 'highRiskEmployeeInfections':
        return records.filter(rec => rec.bucket === 'leaks.logs' && (getUserRiskScore(rec.risk_score, user?.id) >= 3));
      case 'personalComputerInfections':
        return records.filter(
          rec => rec.bucket === 'leaks.logs' && getUserComputerType(rec.computer_type, user?.id) === 'PERSONAL'
        );
      case 'corporateComputerInfections':
        return records.filter(
          rec => rec.bucket === 'leaks.logs' && getUserComputerType(rec.computer_type, user?.id) === 'CORPORATE'
        );
      case 'attackerInfections':
        return records.filter(
          rec => rec.bucket === 'leaks.logs' && getUserComputerType(rec.computer_type, user?.id) === 'ATTACKER'
        );
      case 'thirdPartyInfoStealers':
        return records.filter(
          rec => rec.domain_type === 2 && rec.bucket === 'leaks.logs'
        );
      case 'externalPageCredentials':
        return records.filter(
          rec => rec.external === true && rec.bucket === 'leaks.logs'
        );
      case 'outsourcingInfections':
        return records.filter(
          rec => rec.outsource === true && rec.bucket === 'leaks.logs'
        );
      case 'userInfections':
        return records.filter(
          rec => rec.bucket === 'reverse.leaks.logs' && (rec.url_type & 2)
        );
      default:
        return records;
    }
  }, [activeCounterFilter, user?.id]);

  // Helper function to get masked value for search comparison
  const getMaskedValueForSearch = (field, value, userCredentialVisibility, passwordVisibility = {}) => {
    if (!value) return '';
    
    if (field === 'password') {
      if (!userCredentialVisibility || !passwordVisibility) {
        return `${'*'.repeat(Math.max(0, value.length - 3))}${value.slice(-3)}`;
      }
      return value;
    }
    
    if (field === 'user') {
      if (!userCredentialVisibility) {
        const atIndex = value.indexOf('@');
        if (atIndex === -1 || atIndex <= 5) {
          // If no @ or @ is at position 5 or less, mask all but last 3
          return `${'*'.repeat(Math.max(0, value.length - 3))}${value.slice(-3)}`;
        } else {
          // Mask last 5 characters before @
          const beforeAt = value.substring(0, atIndex);
          const afterAt = value.substring(atIndex);
          const visiblePart = beforeAt.substring(0, Math.max(0, beforeAt.length - 5));
          const maskedPart = '*'.repeat(Math.min(5, beforeAt.length));
          return `${visiblePart}${maskedPart}${afterAt}`;
        }
      }
      return value;
    }
    
    return value;
  };

  const filterAndSortRecords = (records, filters, sortField, sortOrder, domainType, sankeyFilter, selectedDomain) => {
    let filtered = [...records];
    // Apply record type filter first
    if (recordTypeFilter === 'domains') {
      filtered = filtered.filter(rec => rec.domain_type !== undefined);
    } else if (recordTypeFilter === 'urls') {
      filtered = filtered.filter(rec => rec.url_type !== undefined);
    }
    
    // Apply domain type filters only when record type is 'domains'
    if (recordTypeFilter === 'domains') {
      if (domainType === 'primary') {
        filtered = filtered.filter(rec => rec.domain_type === 1);
      } else if (domainType === 'third') {
        filtered = filtered.filter(rec => rec.domain_type === 2);
      }
      
      // Apply specific domain filter if selected
      if (selectedDomain) {
        filtered = filtered.filter(rec => rec.domain === selectedDomain);
      }
    }

    // Apply URL type filters only when record type is 'urls'
    if (recordTypeFilter === 'urls') {
      if (urlTypeFilter === 'employee') {
        filtered = filtered.filter(rec => (Number(rec.url_type) & 1));
      } else if (urlTypeFilter === 'user') {
        filtered = filtered.filter(rec => (Number(rec.url_type) & 2));
      }
      // Specific selection still uses the 'domain' field (which holds URL for URL findings)
      if (selectedDomain) {
        filtered = filtered.filter(rec => rec.domain === selectedDomain);
      }
    }
    // Apply Sankey filter if present
    if (sankeyFilter) {
      if (sankeyFilter.domain) {
        filtered = filtered.filter(rec => {
          // domain, source, or user (see SankeyDiagram)
          const removeExtension = str => {
            if (!str) return 'Unknown';
            const s = str.split('.');
            return s.length > 1 ? s.slice(0, -1).join('.') : str;
          };
          const domainLabel = removeExtension(rec.domain || rec.source || rec.user || 'Unknown');
          return domainLabel === sankeyFilter.domain;
        });
      }
      if (sankeyFilter.computer_type) {
        filtered = filtered.filter(rec => {
          // Sankey uses capitalized first letter
          const cap = v => v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : '';
          return cap(getUserComputerType(rec.computer_type, user?.id)) === sankeyFilter.computer_type;
        });
      }
      if (sankeyFilter.risk_score) {
        filtered = filtered.filter(rec => {
          // Sankey uses risk label, so map risk_score to label
          return getRiskScoreLabel(getUserRiskScore(rec.risk_score, user?.id)) === sankeyFilter.risk_score;
        });
      }
      if (sankeyFilter.bucket) {
        filtered = filtered.filter(rec => rec.bucket === sankeyFilter.bucket);
      }
    }
    Object.entries(filters).forEach(([field, value]) => {
      if (isDateField(field)) {
        if (value?.from) {
          filtered = filtered.filter((rec) => rec[field] && rec[field] >= value.from);
        }
        if (value?.to) {
          filtered = filtered.filter((rec) => rec[field] && rec[field] <= value.to);
        }
      } else if (isTagsField(field)) {
        if (value && value.length > 0) {
          filtered = filtered.filter((rec) => {
            if (!Array.isArray(rec.tags)) return false;
            return rec.tags.some((tag) => value.includes(tag) || value.includes(String(tag)) || value.includes(Number(tag)));
          });
        }
      } else if (isRiskScoreField(field)) {
        if (value) {
          const score = parseInt(value);
          if (!isNaN(score)) {
            filtered = filtered.filter((rec) => getUserRiskScore(rec[field], user?.id) === score);
          }
        }
      } else if (isComputerTypeField(field)) {
        if (value) {
          filtered = filtered.filter((rec) => getUserComputerType(rec[field], user?.id) === value);
        }
      } else if (value) {
        filtered = filtered.filter((rec) => {
          // Map composite fields before masked search
          const compositeValue = field === 'domain_or_url' ? (rec.url || rec.domain || '') : rec[field];
          // For password and user fields, use masked values for search comparison
          const searchValue = getMaskedValueForSearch(field, compositeValue, user?.credential_visibility, tablePasswordVisibility);
          return (searchValue || '').toLowerCase().includes(String(value).toLowerCase());
        });
      }
    });
    filtered.sort((a, b) => {
      // Map composite sort field
      let aVal = sortField === 'domain_or_url' ? (a.url || a.domain || '') : a[sortField];
      let bVal = sortField === 'domain_or_url' ? (b.url || b.domain || '') : b[sortField];
      if (isDateField(sortField)) {
        aVal = aVal || '';
        bVal = bVal || '';
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      } else if (isTagsField(sortField)) {
        aVal = (aVal || []).join(',');
        bVal = (bVal || []).join(',');
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      } else if (isRiskScoreField(sortField)) {
        aVal = getUserRiskScore(aVal, user?.id) || 0;
        bVal = getUserRiskScore(bVal, user?.id) || 0;
        if (sortOrder === 'asc') return aVal - bVal;
        return bVal - aVal;
      } else if (isComputerTypeField(sortField)) {
        aVal = getUserComputerType(aVal, user?.id) || '';
        bVal = getUserComputerType(bVal, user?.id) || '';
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      } else {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      }
    });
    return filtered;
  };

  const filteredTableRecords = useMemo(() => {
    // Apply date filtering first
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    const dateFilteredData = data.filter(rec => {
      // Apply record type filter
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (!rec.added) return true; // Include records without date
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    });
    
    let recs = filterAndSortRecords(dateFilteredData, tableFilters, tableSortField, tableSortOrder, domainTypeFilter, sankeyFilter, selectedDomain);
    recs = applyCounterFilter(recs);
    return recs;
  }, [data, selectedDateRange, tableFilters, tableSortField, tableSortOrder, domainTypeFilter, sankeyFilter, selectedDomain, applyCounterFilter, user?.id, recordTypeFilter, urlTypeFilter]);

  const sidebarFilteredRecords = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedDateRange);
    let recs = data.filter(rec => {
      // Apply record type filter
      if (recordTypeFilter === 'domains' && rec.domain_type === undefined) return false;
      if (recordTypeFilter === 'urls' && rec.url_type === undefined) return false;
      if (!rec.added) return true;
      const recordDate = new Date(rec.added);
      return recordDate >= cutoffDate;
    });
    
    // Apply domain type filters only when record type is 'domains'
    if (recordTypeFilter === 'domains') {
      if (domainTypeFilter === 'primary') {
        recs = recs.filter(rec => rec.domain_type === 1);
      } else if (domainTypeFilter === 'third') {
        recs = recs.filter(rec => rec.domain_type === 2);
      }
      if (selectedDomain) {
        recs = recs.filter(rec => rec.domain === selectedDomain);
      }
    }

    // Apply URL filters only when record type is 'urls'
    if (recordTypeFilter === 'urls') {
      if (urlTypeFilter === 'employee') {
        recs = recs.filter(rec => (Number(rec.url_type) & 1));
      } else if (urlTypeFilter === 'user') {
        recs = recs.filter(rec => (Number(rec.url_type) & 2));
      }
      if (selectedDomain) {
        recs = recs.filter(rec => rec.domain === selectedDomain);
      }
    }
    
    recs = applyCounterFilter(recs);
    return recs;
  }, [data, selectedDateRange, domainTypeFilter, selectedDomain, applyCounterFilter, recordTypeFilter, urlTypeFilter]);

  React.useEffect(() => {
    if (!tableShowTagsDropdown) return;
    const handleClick = (e) => {
      if (!e.target.closest('.tags-dropdown') && !e.target.closest('.tags-button')) {
        setTableShowTagsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tableShowTagsDropdown]);

  React.useEffect(() => {
    if (!tableDatePopover.field) return;
    const handleClick = (e) => {
      if (!e.target.closest('.date-popover') && !e.target.closest('.date-button')) {
        setTableDatePopover({ field: null, anchor: null });
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tableDatePopover]);

  const handleTableSort = (field) => {
    if (tableSortField === field) {
      setTableSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setTableSortField(field);
      setTableSortOrder('asc');
    }
  };
  const handleTableFilterChange = (field, value) => {
    setTableFilters((prev) => ({ ...prev, [field]: value }));
  };
  const handleTableDateFilterChange = (field, bound, value) => {
    setTableFilters((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [bound]: value
      }
    }));
  };
  const handleTableTagsFilterChange = (tagId) => {
    setTableFilters((prev) => {
      const prevTags = prev.tags || [];
      if (prevTags.includes(tagId)) {
        return { ...prev, tags: prevTags.filter((id) => id !== tagId) };
      } else {
        return { ...prev, tags: [...prevTags, tagId] };
      }
    });
  };
  const toggleTablePasswordVisibility = (recordId) => {
    setTablePasswordVisibility((prev) => ({ ...prev, [recordId]: !prev[recordId] }));
  };

  // Memoized handlers for row updates
  const handleSolvedToggle = useCallback((recordId, checked) => {
    setData(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              solved: checked
                ? [...(rec.solved || []), user.id]
                : (rec.solved || []).filter(uid => uid !== user.id),
            }
          : rec
      )
    );
    setPendingSolvedUpdates(prev => ({
      ...prev,
      [recordId]: checked,
    }));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      sendBatchSolvedUpdates();
    }, 2000);
  }, [user.id]);

  // --- Risk Score Change Handler ---
  const handleRiskScoreChange = useCallback((recordId, newScore) => {
    setData(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? { 
            ...rec, 
            risk_score: (() => {
              let arr = Array.isArray(rec.risk_score)
                ? [...rec.risk_score]
                : (typeof rec.risk_score === 'number'
                    ? [{ user_id: 0, risk_score: rec.risk_score }]
                    : []);
              const idx = arr.findIndex(e => e.user_id === user?.id);
              if (idx !== -1) {
                arr[idx] = { user_id: user?.id, risk_score: newScore };
              } else {
                arr.push({ user_id: user?.id, risk_score: newScore });
              }
              return arr;
            })()
          }
          : rec
      )
    );
    setPendingRiskScoreUpdates(prev => ({
      ...prev,
      [recordId]: newScore,
    }));
    if (riskScoreDebounceTimer.current) clearTimeout(riskScoreDebounceTimer.current);
    riskScoreDebounceTimer.current = setTimeout(() => {
      sendBatchRiskScoreUpdates();
    }, 2000);
  }, [user?.id]);

  // --- Computer Type Change Handler ---
  const handleComputerTypeChange = useCallback((recordId, newType, userId) => {
    setData(prevData => {
      // Find the record being updated to get its source
      const updatedRecord = prevData.find(rec => rec.id === recordId);
      if (!updatedRecord) return prevData;
      
      const source = updatedRecord.source;
      
      // Update the specific record and all other records with the same source
      return prevData.map(rec =>
        (rec.id === recordId || (source && rec.source === source))
          ? { ...rec, computer_type: newType }
          : rec
      );
    });
    setPendingComputerTypeUpdates(prev => ({
      ...prev,
      [recordId]: { computer_type: newType, user_id: userId },
    }));
    if (computerTypeDebounceTimer.current) clearTimeout(computerTypeDebounceTimer.current);
    computerTypeDebounceTimer.current = setTimeout(() => {
      sendBatchComputerTypeUpdates();
    }, 2000);
  }, []);

  // Tag management handlers
  const handleTagAdded = useCallback((recordId, tagId) => {
    setData(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              tags: (() => {
                const newTags = [...(rec.tags || []), String(tagId)];
                // Sort by priority (highest first), then alphabetically
                return newTags.sort((a, b) => {
                  const tagA = tagsOptions.find(tag => String(tag.id) === a);
                  const tagB = tagsOptions.find(tag => String(tag.id) === b);
                  const priorityA = tagA?.priority || 0;
                  const priorityB = tagB?.priority || 0;
                  
                  if (priorityA !== priorityB) {
                    return priorityB - priorityA; // Higher priority first
                  }
                  // If same priority, sort alphabetically by name
                  return (tagA?.name || '').localeCompare(tagB?.name || '');
                });
              })()
            }
          : rec
      )
    );
  }, [tagsOptions]);

  const handleTagRemoved = useCallback((recordId, tagId) => {
    setData(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              tags: (rec.tags || []).filter(t => t !== String(tagId))
            }
          : rec
      )
    );
  }, []);

  const sendBatchSolvedUpdates = async () => {
    const updatesObj = pendingSolvedUpdatesRef.current;
    if (Object.keys(updatesObj).length === 0) return;
    const updates = Object.entries(updatesObj).map(([id, checked]) => ({
      id,
      checked,
    }));
    try {
      await api.updateSolvedBatch(updates); 
      setPendingSolvedUpdates({});
    } catch (err) {
      setRowUpdateError('Failed to update solved status');
      console.error('Failed to update solved status', err);
    }
  };

  const sendBatchRiskScoreUpdates = async () => {
    const updatesObj = pendingRiskScoreUpdatesRef.current;
    if (Object.keys(updatesObj).length === 0) return;
    const updates = Object.entries(updatesObj).map(([id, score]) => ({
      id,
      score,
      user_id: user?.id,
    }));
    try {
      await api.updateRiskScoreBatch(updates);
      setPendingRiskScoreUpdates({});
    } catch (err) {
      setRowUpdateError('Failed to update risk scores');
      console.error('Failed to update risk scores', err);
    }
  };

  const sendBatchComputerTypeUpdates = async () => {
    const updatesObj = pendingComputerTypeUpdatesRef.current;
    if (Object.keys(updatesObj).length === 0) return;
    const updates = Object.entries(updatesObj).map(([id, { computer_type, user_id }]) => ({
      id,
      computer_type,
      user_id,
    }));
    try {
      await api.updateComputerTypeBatch(updates);
      setPendingComputerTypeUpdates({});
    } catch (err) {
      setRowUpdateError('Failed to update computer types');
      console.error('Failed to update computer types', err);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        if (Object.keys(pendingSolvedUpdatesRef.current).length > 0) {
          sendBatchSolvedUpdates();
        }
      }
      if (riskScoreDebounceTimer.current) {
        clearTimeout(riskScoreDebounceTimer.current);
        if (Object.keys(pendingRiskScoreUpdatesRef.current).length > 0) {
          sendBatchRiskScoreUpdates();
        }
      }
      if (computerTypeDebounceTimer.current) {
        clearTimeout(computerTypeDebounceTimer.current);
        if (Object.keys(pendingComputerTypeUpdatesRef.current).length > 0) {
          sendBatchComputerTypeUpdates();
        }
      }
    };
  }, []);

  const handleCounterClick = (key) => {
    setActiveCounterFilter(prev => prev === key ? null : key);
  };

  const handleChartToggleExpand = (chartType) => {
    setExpandedChart(prev => prev === chartType ? null : chartType);
  };

  const handleChartVerticalExpand = (chartType, direction) => {
    setChartExpansionLevel(prev => {
      const currentLevel = prev[chartType] || 0;
      const MaxExpansionLevel = 20; // Match the MaxExpansionLevel in SankeyDiagram component
      const newLevel = direction === 'up' ? Math.max(0, currentLevel - 1) : Math.min(MaxExpansionLevel, currentLevel + 1);
      return { ...prev, [chartType]: newLevel };
    });
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleDateRangeChange = (days) => {
    setSelectedDateRange(days);
  };

  const handleDomainTypeChange = (domainType) => {
    setDomainTypeFilter(domainType);
  };

  const handleRecordTypeChange = (recordType) => {
    setRecordTypeFilter(recordType);
    // Reset domain filter when switching record types
    if (recordType !== 'domains') {
      setDomainTypeFilter('all');
      setSelectedDomain('');
    }
    // Reset URL filters when switching record types
    if (recordType !== 'urls') {
      setUrlTypeFilter('all');
    }
  };

  const handleDomainChange = (domain) => {
    setSelectedDomain(domain);
  };

  const showReset = Boolean(activeCounterFilter);

  return (
    <div className="container" style={{ width: '100%', padding: '1.5rem 3vw' }}>
      <DashboardSidebar
        isOpen={sidebarOpen}
        onToggle={handleSidebarToggle}
        selectedDateRange={selectedDateRange}
        onDateRangeChange={handleDateRangeChange}
        recordTypeFilter={recordTypeFilter}
        onRecordTypeChange={handleRecordTypeChange}
        domainTypeFilter={domainTypeFilter}
        onDomainTypeChange={handleDomainTypeChange}
        selectedDomain={selectedDomain}
        onDomainChange={handleDomainChange}
        availableDomains={availableDomains}
        urlTypeFilter={urlTypeFilter}
        onUrlTypeChange={setUrlTypeFilter}
        availableUrls={availableUrls}
      />
      {rowUpdateError && (
        <div style={{
          background: 'var(--danger, #e57373)',
          color: '#fff',
          padding: '10px 16px',
          borderRadius: 6,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 15,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
        }}>
          <span>{rowUpdateError}</span>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontWeight: 700,
              fontSize: 18,
              cursor: 'pointer',
              marginLeft: 12,
              lineHeight: 1
            }}
            onClick={() => setRowUpdateError('')}
            title="Dismiss error"
          >
            ×
          </button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '1rem' }}>

        {!loading && !error && (
          <DashboardCounters
            highRiskEmployeeInfections={highRiskEmployeeInfections}
            personalComputerInfections={personalComputerInfections}
            corporateComputerInfections={corporateComputerInfections}
            thirdPartyInfoStealers={thirdPartyInfoStealers}
            externalPageCredentials={externalPageCredentials}
            outsourcingInfections={outsourcingInfections}
            attackerInfections={attackerInfections}
            userInfections={UserInfections}
            onCounterClick={handleCounterClick}
            activeCounterKey={activeCounterFilter}
            highRiskEmployeeInfectionsUnsolved={highRiskEmployeeInfectionsUnsolved}
            personalComputerInfectionsUnsolved={personalComputerInfectionsUnsolved}
            corporateComputerInfectionsUnsolved={corporateComputerInfectionsUnsolved}
            attackerInfectionsUnsolved={attackerInfectionsUnsolved}
            thirdPartyInfoStealersUnsolved={thirdPartyInfoStealersUnsolved}
            externalPageCredentialsUnsolved={externalPageCredentialsUnsolved}
            outsourcingInfectionsUnsolved={outsourcingInfectionsUnsolved}
            userInfectionsUnsolved={UserInfectionsUnsolved}
          />
        )}
      </div>
      {/* Filters and Reset UI*/}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>

        {/* Filter Display */}
        <span style={{ color: 'var(--text-light)', fontWeight: 500, fontSize: 14, background: '#23293a', borderRadius: 8, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
          {(() => {
            if (recordTypeFilter === 'all') {
              return 'All Records';
            } else if (recordTypeFilter === 'domains') {
              if (selectedDomain) {
                return `Domain: @${String(selectedDomain).replace(/^@+/, '')}`;
              } else if (domainTypeFilter === 'primary') {
                return 'Primary Domains';
              } else if (domainTypeFilter === 'third') {
                return 'Third-Party Domains';
              } else {
                return 'All Domains';
              }
            } else if (recordTypeFilter === 'urls') {
              if (selectedDomain) {
                return `URL: ${selectedDomain}`;
              } else if (urlTypeFilter === 'employee') {
                return 'Employee URLs';
              } else if (urlTypeFilter === 'user') {
                return 'User URLs';
              } else {
                return 'All URLs';
              }
            }
            return 'All Records';
          })()}
        </span>

        {/* Time Interval Display */}
                 <span style={{ color: 'var(--text-light)', fontWeight: 500, fontSize: 14, background: '#23293a', borderRadius: 8, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
           {(() => {
             const endDate = new Date();
             const startDate = new Date();
             startDate.setDate(endDate.getDate() - selectedDateRange);
             
             const formatDate = (date) => {
               const day = String(date.getDate()).padStart(2, '0');
               const month = String(date.getMonth() + 1).padStart(2, '0');
               const year = date.getFullYear();
               return `${day}/${month}/${year}`;
             };
             
             // Determine if we should show years or days
             const isYearRange = selectedDateRange >= 365;
             const timeUnit = isYearRange ? 'years' : 'days';
             const timeValue = isYearRange ? Math.round(selectedDateRange / 365) : selectedDateRange;
             
             return `${formatDate(startDate)} - ${formatDate(endDate)} (${timeValue} ${timeUnit})`;
           })()}
         </span>
        
        {(showReset || sankeyFilter) && (
          <>
            {showReset && (
              <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14, background: '#23293a', borderRadius: 8, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                Label Filter: {(() => {
                  switch (activeCounterFilter) {
                    case 'highRiskEmployeeInfections': return 'High-Risk Infections';
                    case 'personalComputerInfections': return 'Personal Infections';
                    case 'corporateComputerInfections': return 'Corporate Infections';
                    case 'thirdPartyInfoStealers': return 'Third-Party Infections';
                    case 'externalPageCredentials': return 'Employee in External Website';
                    case 'outsourcingInfections': return 'Outsourcing Infections';
                    case 'attackerInfections': return 'Attacker Infections';
                    case 'userInfections': return 'User Infections';
                    default: return '';
                  }
                })()}
                <button
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginLeft: 4, padding: 0 }}
                  onClick={() => setActiveCounterFilter(null)}
                  title="Reset counter filter"
                >
                  ×
                </button>
              </span>
            )}
            {sankeyFilter && (
              <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14, background: '#23293a', borderRadius: 8, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                Sankey Filter:
                {sankeyFilter.domain && <span>Domain: <b>@{String(sankeyFilter.domain).replace(/^@+/, '')}</b></span>}
                {sankeyFilter.computer_type && <span>Computer: <b>{sankeyFilter.computer_type}</b></span>}
                {sankeyFilter.risk_score && <span>Risk: <b>{sankeyFilter.risk_score}</b></span>}
                <button
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginLeft: 4, padding: 0 }}
                  onClick={() => setSankeyFilter(null)}
                  title="Reset Sankey filter"
                >
                  ×
                </button>
              </span>
            )}
          </>
        )}
      </div>
      {error ? (
        <div className="error-message" style={{ color: 'var(--danger)', padding: '1.2rem', textAlign: 'center', fontSize: '1rem' }}>{error}</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '1.2rem', color: 'var(--text-light)', fontSize: '1rem' }}>Loading...</div>
      ) : (
        <>
          {/* Plots */}
          <div style={{ display: 'flex', gap: 32, marginBottom: 32, flexWrap: 'wrap' }}>
            {/* Time Series Plot */}
            <div style={{ 
              flex: expandedChart === 'timeSeries' ? 1 : 1, 
              minWidth: expandedChart === 'timeSeries' ? '100%' : 320, 
              background: 'var(--white)', 
              borderRadius: 8, 
              boxShadow: '0 1px 2px rgba(0,0,0,0.07)', 
              padding: 16,
              display: expandedChart === 'sankey' ? 'none' : 'block'
            }}>
              <h3 style={{ fontSize: 16, marginBottom: 8 }}>Information Stealers Findings</h3>
              <TimeSeriesChart 
                data={timeSeriesData} 
                isExpanded={expandedChart === 'timeSeries'}
                onToggleExpand={() => handleChartToggleExpand('timeSeries')}
                expansionLevel={chartExpansionLevel.timeSeries || 0}
                onVerticalExpand={(direction) => handleChartVerticalExpand('timeSeries', direction)}
              />
            </div>
            {/* Risk Score Bar Chart */}
            <div style={{ 
              flex: expandedChart === 'sankey' ? 1 : 1, 
              minWidth: expandedChart === 'sankey' ? '100%' : 320, 
              background: 'var(--white)', 
              borderRadius: 8, 
              boxShadow: '0 1px 2px rgba(0,0,0,0.07)', 
              padding: 16,
              display: expandedChart === 'timeSeries' ? 'none' : 'block'
            }}>
              <h3 style={{ fontSize: 16, marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                Information Stealers Diagram
                <SankeyDiagramInfo />
              </h3>
              <SankeyDiagram 
                data={sidebarFilteredRecords} 
                onSankeyFilter={setSankeyFilter} 
                sankeyFilter={sankeyFilter}
                currentUserId={user?.id}
                isExpanded={expandedChart === 'sankey'}
                onToggleExpand={() => handleChartToggleExpand('sankey')}
                expansionLevel={chartExpansionLevel.sankey || 0}
                onVerticalExpand={(direction) => handleChartVerticalExpand('sankey', direction)}
              />
            </div>
          </div>
          {/* Unified Table */}
                     <DataTable
             title={`All Records (${filteredTableRecords.length} records)`}
             records={filteredTableRecords}
             tagsOptions={tagsOptions}
             sortField={tableSortField}
             sortOrder={tableSortOrder}
             filters={tableFilters}
             passwordVisibility={tablePasswordVisibility}
             hoveredLink={tableHoveredLink}
             hoveredEye={tableHoveredEye}
             showTagsDropdown={tableShowTagsDropdown}
             datePopover={tableDatePopover}
             onSort={handleTableSort}
             onFilterChange={handleTableFilterChange}
             onDateFilterChange={handleTableDateFilterChange}
             onTagsFilterChange={handleTableTagsFilterChange}
             onTogglePasswordVisibility={toggleTablePasswordVisibility}
             onHoveredLink={setTableHoveredLink}
             onHoveredEye={setTableHoveredEye}
             onShowTagsDropdown={setTableShowTagsDropdown}
             onDatePopover={setTableDatePopover}
             currentUserId={user?.id}
             onSolvedToggle={handleSolvedToggle}
             onRiskScoreChange={handleRiskScoreChange}
             onComputerTypeChange={handleComputerTypeChange}
             userCredentialVisibility={user?.credential_visibility}
             onTagAdded={handleTagAdded}
             onTagRemoved={handleTagRemoved}
           />
        </>
      )}
    </div>
  );
};

export default Dashboard;
